import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dayCheckbox",
    "dayButton",
    "frequencySection",
    "frequencySelect",
    "hiddenFrequencyInput"
  ]

  static values = {
    minWeeks: { type: Number, default: 2 }
  }

    connect() {
    // Initialize frequency section as hidden by default
    this.hideFrequencySection()
    this.updateAvailableDays()
  }

  // Helper: convert JavaScript Date.getDay() (Sunday=0) to our mapping (Monday=0)
  convertToMondayFirst(jsDay) {
    return (jsDay + 6) % 7
  }

    // Event: update available days when dates change
  updateAvailableDays() {
    const dateRange = this.getDateRange()

    if (!dateRange) {
      this.disableAllDays()
      this.hideFrequencySection()
      return
    }

    const weeksDiff = this.calculateWeeksDifference(dateRange.startDate, dateRange.endDate)
    const availableDays = this.getAvailableDaysInRange(dateRange.startDate, dateRange.endDate)

    // ALWAYS enable days that fall within the date range
    this.enableDaysInRange(availableDays)

    // If it's a single day event, auto-select that day
    if (this.isSingleDay(dateRange.startDate, dateRange.endDate)) {
      this.autoSelectSingleDay(dateRange.startDate)
    } else {
      // For multi-day events, clear any previous auto-selections
      // but only if user hasn't manually selected anything
      const hasManualSelections = this.dayCheckboxTargets.some(checkbox => checkbox.checked)
      if (!hasManualSelections) {
        this.clearDaySelections()
      }
    }

    // Configure frequency based on days range
    // Calculate inclusive days (including both start and end dates)
    const daysDiff = Math.ceil(Math.abs(dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24)) + 1

    if (daysDiff <= 7) {
      // 1-7 days: frequency = "once", TomSelect HIDDEN
      this.setFrequencyValue('once')
      this.hideFrequencySection()
    } else if (daysDiff <= 14) {
      // 8-14 days: frequency = "weekly", TomSelect HIDDEN
      this.setFrequencyValue('weekly')
      this.hideFrequencySection()
    } else {
      // 15+ days: User choice between "weekly" or "bi_weekly", TomSelect VISIBLE
      this.showFrequencySection()

      // Set default to weekly if current value is "once" (transitioning from shorter range)
      const currentFrequency = this.getFrequencyValue()
      if (currentFrequency === 'once' || !['weekly', 'bi_weekly'].includes(currentFrequency)) {
        this.setFrequencyValue('weekly')
      }

      // Update display select to reflect current value
      this.syncDisplaySelect()
    }
  }

    // Event: change in selected days
  updateDays(event) {
    const checkbox = event.target
    const index = this.dayCheckboxTargets.indexOf(checkbox)
    const dayButton = this.dayButtonTargets[index]

    // Update button appearance based on checked state
    if (checkbox.checked) {
      this.applyCheckedStyles(dayButton)
    } else {
      this.applyUncheckedStyles(dayButton)
    }

    const selectedDays = this.getSelectedDays()

    if (selectedDays.length > 0) {
      // Frequency options are managed automatically by updateAvailableDays
    } else {
      // No days selected
    }
  }

    // Event: frequency selection change (from display select)
  updateFrequency() {
    // When user changes display select, update the hidden input
    if (this.hasFrequencySelectTarget) {
      const selectedFrequency = this.frequencySelectTarget.value
      this.hiddenFrequencyInputTarget.value = selectedFrequency
    }

    this.validateSelectedFrequency()
  }

  // Helper: get available days within date range
  getAvailableDaysInRange(startDate, endDate) {
    const availableDays = new Set()
    let currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      availableDays.add(this.convertToMondayFirst(currentDate.getDay()))
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return Array.from(availableDays).sort()
  }

              // Helper: enable/disable days based on availability
  enableDaysInRange(availableDays) {
    this.dayCheckboxTargets.forEach((checkbox, index) => {
      const dayIndex = parseInt(checkbox.value)
      const dayButton = this.dayButtonTargets[index]

      if (availableDays.includes(dayIndex)) {
        // Enable day
        checkbox.disabled = false

        // Update button styles for enabled state
        dayButton.classList.remove('bg-gray-50', 'cursor-not-allowed', 'text-gray-400')
        dayButton.classList.add('bg-white', 'cursor-pointer', 'text-gray-700', 'hover:bg-blue-50', 'hover:border-blue-400', 'hover:text-blue-600')

        // If already checked, apply checked styles
        if (checkbox.checked) {
          this.applyCheckedStyles(dayButton)
        }
      } else {
        // Disable day
        checkbox.disabled = true
        checkbox.checked = false

        // Update button styles for disabled state
        dayButton.classList.remove('bg-white', 'cursor-pointer', 'text-gray-700', 'hover:bg-blue-50', 'hover:border-blue-400', 'hover:text-blue-600', 'bg-blue-500', 'border-blue-500', 'text-white')
        dayButton.classList.add('bg-gray-50', 'cursor-not-allowed', 'text-gray-400')
      }
    })
  }

  // Helper: disable all days
  disableAllDays() {
    this.dayCheckboxTargets.forEach((checkbox, index) => {
      checkbox.disabled = true
      checkbox.checked = false
      const dayButton = this.dayButtonTargets[index]

      // Reset button to disabled state
      dayButton.classList.remove('bg-white', 'cursor-pointer', 'text-gray-700', 'hover:bg-blue-50', 'hover:border-blue-400', 'hover:text-blue-600', 'bg-blue-500', 'border-blue-500', 'text-white')
      dayButton.classList.add('bg-gray-50', 'cursor-not-allowed', 'text-gray-400')
    })
  }



  // Helper: show/hide frequency section
  showFrequencySection() {
    this.frequencySectionTarget.classList.remove('hidden')
  }

  hideFrequencySection() {
    this.frequencySectionTarget.classList.add('hidden')
  }

    // Set frequency value programmatically
  setFrequencyValue(frequency) {
    // Always update the hidden input (this is what gets submitted)
    this.hiddenFrequencyInputTarget.value = frequency

    // For 15+ day ranges, also update the display select
    if (frequency !== 'once' && this.hasFrequencySelectTarget) {
      this.frequencySelectTarget.value = frequency
    }
  }

  // Get current frequency value from hidden input
  getFrequencyValue() {
    return this.hiddenFrequencyInputTarget.value
  }

  // Sync display select with current hidden input value
  syncDisplaySelect() {
    if (this.hasFrequencySelectTarget) {
      const currentFrequency = this.getFrequencyValue()
      if (currentFrequency !== 'once') {
        this.frequencySelectTarget.value = currentFrequency
      }
    }
  }



  // Helper: apply checked styles to button
  applyCheckedStyles(button) {
    button.classList.remove('bg-white', 'text-gray-700', 'hover:bg-blue-50', 'hover:border-blue-400', 'hover:text-blue-600')
    button.classList.add('bg-blue-500', 'border-blue-500', 'text-white')
  }

  // Helper: apply unchecked styles to button (enabled state)
  applyUncheckedStyles(button) {
    button.classList.remove('bg-blue-500', 'border-blue-500', 'text-white')
    button.classList.add('bg-white', 'text-gray-700', 'hover:bg-blue-50', 'hover:border-blue-400', 'hover:text-blue-600')
  }

      // Method: validate frequency options based on date range
  validateFrequencyOptions() {
    // This method is now handled by updateAvailableDays()
    // Keep for compatibility if called elsewhere
  }

  // Method: validate currently selected frequency
  validateSelectedFrequency() {
    const frequency = this.frequencySelectTarget.value
    const dateRange = this.getDateRange()

    if (!dateRange || !frequency) return

    const weeksDiff = this.calculateWeeksDifference(dateRange.startDate, dateRange.endDate)

    // Basic validation - could be enhanced later if needed
  }



  // Helper: get date range from form inputs
  getDateRange() {
    const startInput = document.querySelector('[name="single_event[started_at]"]')
    const endInput = document.querySelector('[name="single_event[finished_at]"]')

    if (!startInput?.value || !endInput?.value) return null

    const startDate = new Date(startInput.value)
    const endDate = new Date(endInput.value)

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null

    return { startDate, endDate }
  }

  // Helper: get selected days of week
  getSelectedDays() {
    return Array.from(this.dayCheckboxTargets)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => parseInt(checkbox.value))
  }

    // Helper: calculate weeks difference
  calculateWeeksDifference(startDate, endDate) {
    const diffTime = Math.abs(endDate - startDate)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    // Week calculation for legacy compatibility:
    // Note: Main logic now uses days-based validation
    // 1-7 days = once (no recurrence)
    // 8-14 days = weekly only
    // 15+ days = weekly or bi-weekly options
    const diffWeeks = Math.floor(diffDays / 7)

    return diffWeeks
  }

  clearRecurringData() {
    this.dayCheckboxTargets.forEach(checkbox => checkbox.checked = false)
    this.setFrequencyValue('once')
  }

  // Helper: clear day selections only (not the enabled/disabled state)
  clearDaySelections() {
    this.dayCheckboxTargets.forEach((checkbox, index) => {
      if (checkbox.checked) {
        checkbox.checked = false
        const dayButton = this.dayButtonTargets[index]
        if (!checkbox.disabled) {
          this.applyUncheckedStyles(dayButton)
        }
      }
    })
  }

  // Helper: check if event is a single day
  isSingleDay(startDate, endDate) {
    return startDate.toDateString() === endDate.toDateString()
  }

    // Helper: auto-select the day for single-day events
  autoSelectSingleDay(date) {
    const dayOfWeek = this.convertToMondayFirst(date.getDay()) // Convert to Monday=0 mapping

    // Clear all previous selections first
    this.dayCheckboxTargets.forEach((checkbox, index) => {
      if (checkbox.checked) {
        checkbox.checked = false
        const dayButton = this.dayButtonTargets[index]
        this.applyUncheckedStyles(dayButton)
      }
    })

    // Find the checkbox for this day of week
    const targetCheckbox = this.dayCheckboxTargets.find(checkbox =>
      parseInt(checkbox.value) === dayOfWeek
    )

    if (targetCheckbox && !targetCheckbox.disabled) {
      targetCheckbox.checked = true

      // Apply visual styles
      const index = this.dayCheckboxTargets.indexOf(targetCheckbox)
      const dayButton = this.dayButtonTargets[index]
      this.applyCheckedStyles(dayButton)

      // Frequency options are automatically validated in updateAvailableDays
    }
  }

    // Handle date change from easepick
  handleDateChange() {
    this.updateAvailableDays()
  }
}
